/* global Messenger */
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useDropzone } from 'react-dropzone';

// Components
import FeedbackCard from '../FeedbackCard';
import { Dialog } from '../Dialog';
import { DialogHeader } from '../DialogHeader';
import { DialogBody } from '../DialogBody';
import { DialogFooter } from '../DialogFooter';
import { Button } from '../Button';
import { EditableList } from '../EditableList';
import { Badge } from '../Badge';
import { Spinner } from '../Spinner';
import { MaterialIcon } from '../MaterialIcon';
import { ConfirmationModal } from '../ConfirmationModal';

const getColor = (props) => {
    if (props.isDragAccept) {
        return '#00e676';
    }
    if (props.isDragReject) {
        return '#ff1744';
    }
    if (props.isFocused) {
        return '#2196f3';
    }
    return '#eeeeee';
};

const Container = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 4rem 20px;
    border-width: 2px;
    border-radius: 2px;
    border-color: ${(props) => getColor(props)};
    border-style: dashed;
    background-color: #fafafa;
    color: #bdbdbd;
    outline: none;
    transition: border 0.24s ease-in-out;
    cursor: pointer;
`;

const ButtonContents = styled.div`
    display: flex;
    justify-content: space-between;
    gap: 6px;
    align-items: center;
`;

const SelectedFileListContainer = styled.div`
    margin-top: 3rem;
`;

const SelectedFileListTitle = styled.h4`
    font-size: 1rem;
    font-weight: 500;
`;

const FileSize = styled.span`
    font-weight: 500;
    font-size: 1rem;
    margin-left: 0.5rem;
`;

const FileLabel = styled.div`
    display: flex;
    align-items: center;
    flex: 1;
    justify-content: space-between;
`;

const readableFileSize = function(bytes) {
    if (bytes === 0) {
        return '0.00 B';
    }

    let e = Math.floor(Math.log(bytes) / Math.log(1024));
    return (bytes / Math.pow(1024, e)).toFixed(2) + ' ' + ' KMGTP'.charAt(e) + 'B';
};

export const FileImportDialog = ({
    acceptedMIMETypesMap,
    closeDialog,
    description,
    isImporting,
    hasImported,
    onImport,
    settledUploads = [],
    title
}) => {
    const [files, setFiles] = React.useState({});
    const [closeConfirmation, setCloseConfirmation] = React.useState(false);
    const [fatalError, setFatalError] = React.useState(null);
    const { getRootProps, getInputProps, isFocused, isDragAccept, isDragReject } = useDropzone({
        accept: acceptedMIMETypesMap,
        onDrop: (acceptedFiles) => {
            setFiles({
                ...files,
                ...acceptedFiles.reduce(
                    (acc, file) => ({
                        ...acc,
                        [file.name]: Object.assign(file, {
                            preview: window.URL.createObjectURL(file)
                        })
                    }),
                    {}
                )
            });
        }
    });

    const filesArray = React.useMemo(() => {
        return Object.entries(files).map(([fileKey, file]) => ({
            key: fileKey,
            label: (
                <FileLabel>
                    <span>
                        {fileKey} <FileSize>[{readableFileSize(file.size)}]</FileSize>
                    </span>
                    {(isImporting || hasImported) && !settledUploads.some((id) => fileKey.includes(id)) ? (
                        <Spinner size={18} color={'#1E527E'} />
                    ) : null}
                    {(isImporting || hasImported) && settledUploads.some((id) => fileKey.includes(id)) ? (
                        <MaterialIcon size={18} icon={'done_outline'} intent={'success'} />
                    ) : null}
                </FileLabel>
            )
        }));
    }, [files, settledUploads, isImporting]);
    const onClickClose = React.useCallback(() => {
        if (filesArray.length) {
            setCloseConfirmation(true);
        } else {
            closeDialog();
        }
    }, [closeDialog, filesArray, closeConfirmation, setCloseConfirmation]);

    const removeFile = React.useCallback(
        (keyForRemoval) => {
            const filesCopy = { ...files };
            delete filesCopy[keyForRemoval];
            setFiles(filesCopy);
        },
        [files, setFiles]
    );

    return (
        <>
            <div>
                <Dialog
                    // style={{ height: '90vh', width: '90vw', maxWidth: '100rem', maxHeight: '100rem' }}
                    close={onClickClose}
                    size="lg"
                    visible
                    id="file-import-dialog"
                >
                    <DialogHeader close={onClickClose}>{title}</DialogHeader>
                    <DialogBody style={{ overflowX: 'hidden', flex: '1', maxHeight: 'none' }}>
                        <div>
                            {fatalError != null && (
                                <FeedbackCard
                                    intent={'danger'}
                                    feedback={{
                                        heading: `An error has occurred.`,
                                        message:
                                            typeof fatalError === 'string'
                                                ? fatalError
                                                : `Please wait a few moments before trying your request
again.`
                                    }}
                                    details={fatalError}
                                />
                            )}
                            {description ? <p style={{ margin: `0 0 1.33rem 0` }}>{description}</p> : null}
                            <Container {...getRootProps({ isFocused, isDragAccept, isDragReject })}>
                                <input {...getInputProps()} />
                                <p>Drag 'n' drop some files here, or click to select files</p>
                            </Container>
                            {filesArray.length ? (
                                <SelectedFileListContainer>
                                    <SelectedFileListTitle>Selected Files</SelectedFileListTitle>
                                    <EditableList listItems={filesArray} removeItem={removeFile} />
                                </SelectedFileListContainer>
                            ) : null}
                        </div>
                    </DialogBody>
                    <DialogFooter>
                        <Button
                            text
                            appearance="primary"
                            disabled={!filesArray.length || isImporting}
                            loading={isImporting || hasImported}
                            onClick={() => {
                                onImport(Object.values(files)).then(
                                    () => {
                                        setTimeout(() => {
                                            Messenger.showMessage({
                                                type: `success`,
                                                title: 'Criteria imported successfully.'
                                            });
                                            closeDialog();
                                        }, 500);
                                    },
                                    (e) => {
                                        const mtsErrMessage = e?.response?.data?.propertyMap?.message ?? e;

                                        if (typeof mtsErrMessage === 'string') {
                                            Messenger.showMessage({
                                                type: `error`,
                                                title: mtsErrMessage
                                            });
                                        }
                                        setFatalError(mtsErrMessage);
                                    }
                                );
                            }}
                        >
                            <ButtonContents>
                                Import Files {filesArray.length ? <Badge>{filesArray.length}</Badge> : null}
                            </ButtonContents>
                        </Button>
                        <Button text appearance="secondary" onClick={onClickClose}>
                            Cancel
                        </Button>
                    </DialogFooter>
                </Dialog>
            </div>
            {closeConfirmation ? (
                <ConfirmationModal
                    header={`Cancel Import?`}
                    body={`There ${
                        filesArray.length > 1 ? 'are several files staged which have' : 'is a file staged which has'
                    } not yet been imported. Are you sure you want cancel the import?`}
                    confirmButtonText={'Yes'}
                    closeDialog={() => {
                        setCloseConfirmation(false);
                    }}
                    onConfirm={() => {
                        return new Promise((resolve) => {
                            closeDialog();
                        });
                    }}
                />
            ) : null}
        </>
    );
};

export default FileImportDialog;

FileImportDialog.propTypes = {
    acceptedMIMETypesMap: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string)).isRequired,
    closeDialog: PropTypes.func,
    description: PropTypes.string,
    isImporting: PropTypes.bool,
    hasImported: PropTypes.bool,
    onImport: PropTypes.func.isRequired,
    settledUploads: PropTypes.arrayOf(PropTypes.string),
    title: PropTypes.string
};
